import { Button, RadioBoxInput } from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import type { InsuranceRelatedMatterPlacement } from "@/lib/data/requests/matters/matter-insurance";
import { useEffect, useState } from "react";

interface CopyDestination {
  clientId: null | string;
  clientName: null | string;
  id: string;
  title: string;
}

const placementOptions = [
  { label: "Yes", value: "client" },
  { label: "No", value: "other_party" },
] as const;

type Placement = InsuranceRelatedMatterPlacement["placement"];

export function InsuranceCopyModeModal({
  destinations,
  open,
  onOpenChange,
  onSubmit,
  loading = false,
}: {
  destinations: CopyDestination[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (placements: InsuranceRelatedMatterPlacement[]) => void;
  loading?: boolean;
}) {
  const [placementsByMatterId, setPlacementsByMatterId] = useState<
    Record<string, Placement>
  >({});

  useEffect(() => {
    if (open) {
      setPlacementsByMatterId(
        Object.fromEntries(
          destinations.map(({ id }) => [id, "client" as const]),
        ),
      );
    }
  }, [destinations, open]);

  const setPlacement = (destination: CopyDestination, placement: Placement) => {
    setPlacementsByMatterId((current) => {
      const next = { ...current };
      const matchingMatterIds = destination.clientId
        ? destinations
            .filter(({ clientId }) => clientId === destination.clientId)
            .map(({ id }) => id)
        : [destination.id];
      for (const matterId of matchingMatterIds) {
        next[matterId] = placement;
      }
      return next;
    });
  };

  return (
    <AppModal
      open={open}
      onOpenChange={onOpenChange}
      title="Are any of these clients insured by this policy?"
      description="Choose one role for each destination client."
      size="lg"
      overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
      className="z-[109]"
      loading={loading}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={loading || destinations.length === 0}
            onClick={() => {
              onSubmit(
                destinations.map(({ id }) => ({
                  matterId: id,
                  placement: placementsByMatterId[id] ?? "client",
                })),
              );
            }}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="divide-border divide-y rounded-lg border">
        {destinations.map((destination) => {
          return (
            <div key={destination.id} className="space-y-3 px-4 py-3">
              <p className="min-w-0 text-sm">
                <span className="font-medium">{destination.title}</span>
                <span className="text-muted-foreground">
                  {" — "}
                  {destination.clientName || "Client unavailable"}
                </span>
              </p>
              <RadioBoxInput
                ariaLabel={`Insurance role for ${destination.title}`}
                className="sm:grid-cols-2"
                name={`insurance-copy-${destination.id}`}
                options={placementOptions}
                value={placementsByMatterId[destination.id] ?? "client"}
                disabled={loading}
                onValueChange={(placement) => {
                  setPlacement(destination, placement);
                }}
              />
            </div>
          );
        })}
      </div>
    </AppModal>
  );
}
